import React, { useState } from 'react'
import { FormControlLabel, Grid, TextField, Typography } from '@material-ui/core'
import { Moment } from 'moment'
import { DateTimePicker } from '@material-ui/pickers'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'

import { Dialog, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'

import { CreateEventPayload, CreateEventResponse, Event } from '../../types'
import { createEvent } from '../../api'
import { CustomSwitch } from '../CustomSwitch'
import { useStyles } from './styles'

const rules = {
  name: [
    {
      validate: (name?: string) => !!name && name.length <= 50,
      message: 'No puede superar los 50 caracteres',
    },
  ],
  start: [
    {
      validate: (start?: Moment) => !!start,
      message: 'Seleccionar una fecha de incio',
    },
  ],
  end: [
    {
      validate: (start?: Moment) => !!start,
      message: 'Seleccionar una fecha de fin',
    },
  ],
}

interface CreateEventDialogProps {
  handleClose: () => void
  onDone: (event: Event) => void
}

const CreateEventDialog: React.FC<CreateEventDialogProps> = ({ handleClose, onDone }) => {
  const classes = useStyles()

  const [name, setName] = useState<string>()
  const [active, setActive] = useState(false)
  const [start, setStart] = useState<Moment>()
  const [end, setEnd] = useState<Moment>()

  const [createEventApi, isLoading] = useApiCall<CreateEventPayload, CreateEventResponse>(
    createEvent,
  )

  const handleChangeName: React.ChangeEventHandler<HTMLInputElement> = e => {
    setName(e.target.value)
  }

  const changeStartDate = (date: MaterialUiPickersDate) => {
    setStart(date as Moment)
  }

  const changeEndDate = (date: MaterialUiPickersDate) => {
    setEnd(date as Moment)
  }

  const toggleActive = () => {
    setActive(!active)
  }

  const fields = {
    name,
    start,
    end,
    active,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(fields, rules, [
    'name',
    'start',
    'end',
  ])
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading

  const onCreateEvent = async () => {
    if (!name || !start || !end || active === undefined) return

    try {
      const { event } = await createEventApi({
        name,
        start,
        end,
        active,
      })

      onDone(event)
    } catch (error) {
      snackbar.show('No se pudo crear el sorteo. Intente de nuevo.')
    }
  }

  return (
    <Dialog
      title="Nuevo sorteo"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateEvent}
      className={classes.modal}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Nombre"
            value={name}
            fullWidth
            onChange={handleChangeName}
            disabled={isLoading}
            required
            error={errors.name.hasError}
            helperText={errors.name.message}
          />
        </Grid>
        <Grid item xs={12}>
          <DateTimePicker
            label="Desde"
            value={start ?? null}
            onChange={changeStartDate}
            ampm={false}
            TextFieldComponent={props => <TextField {...props} />}
            format="DD/MM/YYYY HH:mm"
            clearable
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <DateTimePicker
            label="Hasta"
            value={end ?? null}
            onChange={changeEndDate}
            ampm={false}
            TextFieldComponent={props => <TextField {...props} />}
            format="DD/MM/YYYY HH:mm"
            clearable
            fullWidth
            required
          />
        </Grid>
        <Grid container direction="column" item xs={12}>
          <FormControlLabel
            control={<CustomSwitch checked={active} onChange={toggleActive} required />}
            label="Activo"
          />
          <Typography variant="caption">*Solo puede haber un evento activado</Typography>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { CreateEventDialog }
